import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ReCAPTCHA from "react-google-recaptcha";
import api from "../api/api.js";
import styles from "./ForgotPassword.module.css";
import { forgotPasswordSchema } from "../lib/validations";

const ForgotPassword = () => {
  const [message, setMessage]           = useState("");
  const [isError, setIsError]           = useState(false);
  const [loading, setLoading]           = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const onSubmit = async (data) => {
    setMessage("");

    if (!captchaToken) {
      setIsError(true);
      setMessage("Por favor completa el CAPTCHA.");
      return;
    }

    try {
      setLoading(true);
      const { data: resp } = await api.post("/request_reset", { mail: data.email });
      setIsError(false);
      setMessage(typeof resp === 'string' ? resp : "Si el correo existe, recibirás instrucciones para restablecer tu contraseña.");
    } catch (err) {
      setIsError(true);
      setMessage(
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data ||
        "Error al solicitar recuperación. Intenta de nuevo."
      );
    } finally {
      setLoading(false);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  return (
    <div className={styles.background}>
      <div className={styles.container}>
        <h2 className={styles.title}>Recuperar Contraseña</h2>
        <p className={styles.subtitle}>
          Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="email" className={styles.label}>Correo Electrónico</label>
            <input
              id="email"
              type="email"
              placeholder="correo@ejemplo.com"
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
              aria-describedby={errors.email ? 'email-error' : undefined}
              aria-invalid={errors.email ? 'true' : undefined}
              {...register('email')}
            />
            {errors.email && (
              <span id="email-error" className={styles.fieldError} role="alert">
                {errors.email.message}
              </span>
            )}
          </div>

          <div className={styles.captchaWrapper}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
            />
          </div>

          {message && (
            <p className={isError ? styles.messageError : styles.messageSuccess}>
              {message}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !captchaToken}
            className={styles.button}
          >
            {loading ? "Procesando..." : "Enviar enlace de recuperación"}
          </button>
        </form>

        <p className={styles.backLink}>
          <span onClick={() => navigate("/login")}>Volver al inicio de sesión</span>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
